import type { MessageStream } from "@/lib/api/MessageStream";
import type {
	V1DSyncStream,
	VintApi,
	VintApiOptions,
	VintV1AiBallotParsingRequest,
	VintV1AiBallotParsingResponse
} from "@/lib/api/VintApi";
import debug from "debug";
import type {
	V1DetermineWkRequest,
	V1DetermineWkResponse,
	V1DetermineWksMessage,
	V1DetermineWksMessageTypeResponse
} from "@/lib/api/VotingDistricts";
import { isV1DetermineWksMessageTypeResponse } from "@/lib/api/VotingDistricts";

const logger = debug("vint-api");

export class VintApiImpl implements VintApi {
	constructor(private options: VintApiOptions) {
	}

	public async v1UseCredential(credential: string): Promise<{
		token: string;
	}> {
		logger("credential", credential);

		const resp = await fetch(`${this.options.endpoint}/api/v1/use-credential`, {
			body: JSON.stringify({ credential: credential }),
			headers: {
				"Content-Type": "application/json"
			},
			method: "POST",
			credentials: "omit"
		});

		const code = resp.status;
		if (code !== 200) {
			throw new Error(`Failed to validate token: ${code}`);
		} else {
			return await resp.json();
		}
	}

	public async v1DetermineWk(request: V1DetermineWkRequest, token: string)
		: Promise<V1DetermineWkResponse> {
		const resp = await fetch(`${this.options.endpoint}/api/v1/determine-wk`, {
			body: JSON.stringify(request),
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${token}`
			},
			method: "POST",
			credentials: "omit"
		});
		return await this.parseResponse(resp);
	}

	v1DetermineWks(token: string): {
		determineWk: (address: string) => Promise<V1DetermineWksMessageTypeResponse>,
		close: () => void
	} {

		const stream = this.messageStream<V1DetermineWksMessage>(`${this.options.webSocketEndpoint}/api/v1/determine-wks?token=${token}`);

		const queue: {
			address: string,
			errCb: (err: any) => void,
			okCb: (arg: V1DetermineWksMessageTypeResponse) => void
		}[] = [];
		let current: {
			address: string,
			errCb: (err: any) => void,
			okCb: (arg: V1DetermineWksMessageTypeResponse) => void
		} | undefined = undefined;

		stream.onMessage(arg => {
			if (isV1DetermineWksMessageTypeResponse(arg)) {
				logger(`Received response for request ${current?.address}: ${arg.address?.fullAddress}`);
				current?.okCb(arg);
				current = undefined;
				next();
			} else {
				logger(`Received invalid response for request ${current?.address}`);
				current?.errCb(new Error("Invalid response"));
				current = undefined;
				next();
			}
		});

		stream.onClose(() => {
			if (current)
				current.errCb(new Error("WebSocket closed"));
			for (const entry of queue) {
				entry.errCb(new Error("WebSocket closed"));
			}
			current = undefined;
			queue.length = 0;
		});

		function next() {
			if (current !== undefined)
				return;

			const next = queue.shift();

			if (!next) {
				return;
			}

			current = next;

			logger(`Sending request for ${next.address}`);
			stream.sendMessage({
				address: next.address
			});
		}

		return {
			determineWk(address: string) {
				return new Promise<V1DetermineWksMessageTypeResponse>((okCb, errCb) => {
					queue.push({
						address,
						errCb,
						okCb
					});
					next();
				});
			},
			close() {
				stream.close();
			}
		};
	}

	private async parseResponse<T>(resp: Response): Promise<T> {
		const code = resp.status;
		if (code !== 200) {
			throw new Error(`Failed to determine WK: ${code}`);
		} else {
			return await resp.json() as T;
		}
	}

	messageStream<T>(url: string): MessageStream<T> {
		const webSocket = new WebSocket(url);

		const sendQueue: T[] = [];

		let onMessageCb: ((arg: T) => void) | undefined = undefined;
		let onCloseCb: (() => void) | undefined = undefined;
		let onOpenCb: (() => void) | undefined = undefined;

		const messageStream: MessageStream<T> = {
			onMessage(callback: (arg: T) => void) {
				onMessageCb = callback;
			},
			sendMessage(type: T) {
				if (webSocket.readyState !== WebSocket.OPEN) {
					sendQueue.push(type);
				} else {
					webSocket.send(JSON.stringify(type));
				}
			},
			onOpen(callback: () => void) {
				onOpenCb = callback;
			},
			onClose(callback: () => void) {
				onCloseCb = callback;
			},
			close() {
				webSocket.close();
			}
		};

		webSocket.onmessage = (ev: MessageEvent) => {
			const data = JSON.parse(ev.data);
			if (onMessageCb) {
				onMessageCb(data);
			}
		};

		webSocket.onopen = (ev: Event) => {
			logger("WebSocket opened");
			while (sendQueue.length > 0) {
				const next = sendQueue.shift();
				if (next !== undefined) {
					logger(`Dequeueing message ${JSON.stringify(next)}`);
					webSocket.send(JSON.stringify(next));
				}
			}
			onOpenCb?.();
		};

		webSocket.onerror = (ev: Event) => {
			console.error("WebSocket error", ev);
			sendQueue.length = 0;
			webSocket.close();
		};

		webSocket.onclose = (ev: CloseEvent) => {
			logger("WebSocket closed", ev);
			sendQueue.length = 0;
			onCloseCb?.();
		};

		return messageStream;
	}

	async v1DSync(token: string): Promise<V1DSyncStream> {
		return this.messageStream(`${this.options.webSocketEndpoint}/api/v1/d-sync?token=${token}`);
	}

	async v1AiBallotParsing(request: VintV1AiBallotParsingRequest, token: string): Promise<VintV1AiBallotParsingResponse> {
		const resp = await fetch(`${this.options.endpoint}/api/v1/ai-ballot-parsing`, {
			body: JSON.stringify({
				image: request.image,
				candidates: request.candidates
			}),
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${token}`
			},
			method: "POST",
			credentials: "omit"
		});
		return await this.parseResponse(resp);
	}

}
